import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowMonitoringApiService } from '../services/workflow-monitoring-api.service';
import { WorkflowRecordHistoryResponse, WorkflowRecordHistoryItem } from '../models/workflow-monitoring.model';
import { WorkflowTimelineModalComponent } from '../workflow-timeline-modal/workflow-timeline-modal.component';
import { workflowExitReasonLabel, workflowObjectTypeLabel, workflowStatusLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-record-history',
  standalone: true,
  imports: [CommonModule, FormsModule, WorkflowTimelineModalComponent],
  templateUrl: './workflow-record-history.component.html'
})
export class WorkflowRecordHistoryComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Etiquetas legibles: diccionario único del módulo (`models/workflow-labels.ts`). Se usan como
   * RESPALDO de los campos `*Label` que resuelve el backend — cuando el DTO no los trae, la
   * plantilla pintaba el enum del motor en crudo.
   */
  readonly statusText = workflowStatusLabel;
  readonly exitReasonText = workflowExitReasonLabel;

  @Input() objectType!: string;
  @Input() objectId!: number;

  history: WorkflowRecordHistoryResponse | null = null;
  loading = false;
  error = '';
  includeTestRuns = false;
  includeSteps = false;

  timelineEnrollmentId: number | null = null;

  private destroy$ = new Subject<void>();

  constructor(private monitoringApi: WorkflowMonitoringApiService) {}

  ngOnInit(): void { this.load(); }
  ngOnChanges(): void { if (this.objectType && this.objectId) this.load(); }
  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  load(): void {
    if (!this.objectType || !this.objectId) return;
    this.loading = true;
    this.error = '';
    this.monitoringApi.getRecordWorkflowHistory(this.objectType, this.objectId, {
      includeTestRuns: this.includeTestRuns,
      includeSteps: this.includeSteps
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.history = res; this.loading = false; },
      error: (err) => {
        this.error = err.error?.message ?? 'No se pudo cargar el historial.';
        this.loading = false;
      }
    });
  }

  /**
   * inc40 — cómo se identifica el registro en la cabecera.
   *
   * El backend resuelve el nombre y la etiqueta del tipo; si el registro ya no existe (o
   * todavía no llegó la respuesta) se cae al tipo traducido + id, nunca al enum crudo.
   */
  get registroLabel(): string {
    const tipo = this.history?.objectTypeLabel || this.tipoFallback;
    const nombre = this.history?.objectName;
    return nombre ? `${tipo}: ${nombre} · #${this.objectId}` : `${tipo} #${this.objectId}`;
  }

  /**
   * Traducción para el instante previo a que responda el backend. Usa el diccionario único del
   * módulo en vez de un mapa propio.
   */
  private get tipoFallback(): string {
    return workflowObjectTypeLabel(this.objectType, this.objectType);
  }

  getStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':    return 'bg-green-100 text-green-700';
      case 'COMPLETED': return 'bg-blue-100 text-blue-700';
      case 'FAILED':    return 'bg-red-100 text-red-700';
      case 'CANCELLED': return 'bg-gray-200 text-gray-600';
      case 'EXITED':    return 'bg-gray-100 text-gray-500';
      case 'WAITING':   return 'bg-yellow-100 text-yellow-700';
      default:          return 'bg-gray-100 text-gray-500';
    }
  }

  isTestRun(item: WorkflowRecordHistoryItem): boolean {
    return item.enrollmentSource === 'TEST_RUN';
  }
}
